import { useContext, useEffect } from "react";
import { Button } from "@/components/atoms/Button";
import { LogIn, PanelLeftClose, PencilLine } from "lucide-react";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/atoms/Tooltip";
import ChatItem from "@/components/molecules/ChatItem";
import { twMerge } from "tailwind-merge";
import NavigationContext from "@/contexts/navigation";
import { useActions, useSignal } from "@rasenganjs/signals";
import { ChatsActions, ChatsState } from "@/store/signals/chats";

export default function Sidebar() {
	const { open, toggleDrawer, closeDrawer, openDrawer } =
		useContext(NavigationContext);

	// States
	const { chats, selectedChatId } = useSignal<ChatsState>("chats");

	// Actions
	const { reset, selectChat } = useActions<ChatsActions>("chats");

	useEffect(() => {
		if (typeof window === "undefined") return;

		handleResize();

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [open]);

	// Handlers
	const handleResetForNewChat = () => {
		reset();
	};

	const handleResize = () => {
		if (window.innerWidth <= 768) {
			closeDrawer();
		} else {
			openDrawer();
		}
	};

	return (
		<aside
			className={twMerge(
				"w-[250px] z-30 flex h-screen md:h-content p-4 pt-0 md:p-0 md:pr-4 flex-col justify-between bg-background text-foreground transition-all duration-300",
				open
					? "translate-x-0 absolute md:relative"
					: "-translate-x-[100%] absolute"
			)}
		>
			<div className='flex flex-col w-full'>
				<div className='flex items-center justify-between mt-4'>
					{/* <AppLogo size="small" /> */}

					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger aria-label='Toggle sidebar'>
								<div
									onClick={toggleDrawer}
									className='w-10 h-10 flex items-center justify-center rounded-full bg-card/70 cursor-pointer'
								>
									<PanelLeftClose size={20} />
								</div>
							</TooltipTrigger>
							<TooltipContent>
								<p>Close sidebar</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>

					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger aria-label='New chat'>
								<div
									onClick={handleResetForNewChat}
									className='w-10 h-10 flex items-center justify-center rounded-full bg-card/70 cursor-pointer'
								>
									<PencilLine size={20} />
								</div>
							</TooltipTrigger>
							<TooltipContent>
								<p>New chat</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>

				<div className='flex flex-col mt-4 gap-1'>
					<span className='text-sm mb-4'>History</span>

					{chats.map((chat) => (
						<ChatItem
							key={chat.id}
							chat={chat}
							onSelect={selectChat}
							active={chat.id === selectedChatId}
						/>
					))}
				</div>
			</div>

			<div className='w-full mb-4'>
				<Button className='w-full'>
					<LogIn size={20} className='mr-2 text-primary-foreground' />

					<span>Register</span>
				</Button>
			</div>
		</aside>
	);
}
